import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, BookOpen } from "lucide-react";
import { browserLocale } from "@/lib/locale";

const DUTCH_POSTS = [
  {
    slug: "herstel-na-trauma-de-eerste-stap",
    title: "Herstel na trauma: de eerste stap",
    excerpt: "Herstel verloopt niet in een rechte lijn — hoe het begin eruitziet en wat helpt.",
  },
  {
    slug: "aardingsoefeningen-terug-in-het-hier-en-nu",
    title: "Aardingsoefeningen",
    excerpt: "Eenvoudige oefeningen die je overal kunt doen, zodat je lichaam terugkeert naar nu.",
  },
  {
    slug: "je-zenuwstelsel-kalmeren",
    title: "Je zenuwstelsel kalmeren",
    excerpt: "Waarom je lichaam sneller reageert dan je denkt, en wat het langzaam rustiger maakt.",
  },
] as const;

/**
 * Articles written in Dutch, shown only to readers on the Dutch pages.
 * The language is read in the browser, so the section never appears in the
 * English, German, French, Spanish, Chinese, Arabic or Hindi versions.
 */
export function DutchReadingSection() {
  const [isDutch, setIsDutch] = useState(false);
  useEffect(() => setIsDutch(browserLocale() === "nl"), []);
  if (!isDutch) return null;

  return (
    <section
      data-no-translate
      dir="ltr"
      className="rounded-2xl border border-gold/25 bg-gold-light/15 p-5 sm:p-6"
    >
      <h2 className="flex items-center gap-2 font-display text-xl text-foreground sm:text-2xl">
        <BookOpen className="h-5 w-5 text-[var(--gold-deep)]" />
        Lezen in het Nederlands
      </h2>
      <p className="mt-1.5 text-sm leading-relaxed text-foreground/75">
        Artikelen over herstel na trauma, aardingsoefeningen en het kalmeren van je zenuwstelsel —
        in gewoon Nederlands.
      </p>

      <ul className="mt-4 grid gap-3 sm:grid-cols-3">
        {DUTCH_POSTS.map((post) => (
          <li key={post.slug}>
            <Link
              to="/blog/$slug"
              params={{ slug: post.slug }}
              className="group flex h-full flex-col rounded-xl border border-border bg-card p-4 transition-colors hover:border-gold/40"
            >
              <span className="font-medium leading-snug text-foreground">{post.title}</span>
              <span className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                {post.excerpt}
              </span>
              <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[var(--gold-deep)]">
                Lezen
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </span>
            </Link>
          </li>
        ))}
      </ul>

      <Link
        to="/blog"
        className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[var(--gold-deep)]"
      >
        Alle artikelen
        <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </section>
  );
}
